import type { AdminMeta, LocalizedName } from '@numistic/contracts'
import { useTranslation } from '@numistic/i18n'
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Checkbox,
  Field,
  Input,
  Table,
  TBody,
  Td,
  Th,
  THead,
  Tr,
} from '@numistic/ui'
import { useNavigate } from '@tanstack/react-router'
import { useState } from 'react'

import {
  ActiveToggle,
  AdminError,
  NameInputs,
  OriginBadge,
  RemoveButton,
  UsageBadge,
  useAdminAction,
} from './common.js'

/** Строка списка: название (правится по месту), код, происхождение, использование, активность. */
function Row({
  name,
  code,
  origin,
  customized,
  usage,
  active,
  extra,
  onRename,
  onActive,
  onRemove,
  canDeactivate = true,
}: {
  name: LocalizedName
  code: string
  origin: string
  customized: boolean
  usage: number
  active: boolean
  extra?: React.ReactNode
  onRename: (name: LocalizedName) => void
  onActive: (active: boolean) => void
  onRemove: () => void
  canDeactivate?: boolean
}) {
  const { t } = useTranslation()
  const [value, setValue] = useState(name.ru)
  return (
    <Tr>
      <Td>
        <Input
          aria-label={t('adminMeta.nameFor', { code })}
          value={value}
          className={active ? '' : 'text-muted-foreground line-through'}
          onChange={(e) => setValue(e.target.value)}
          onBlur={() =>
            value.trim() && value !== name.ru && onRename({ ...name, ru: value.trim() })
          }
        />
      </Td>
      <Td className="font-mono text-xs text-muted-foreground">{code}</Td>
      {extra}
      <Td>
        <UsageBadge usage={usage} />
      </Td>
      <Td>
        <div className="flex items-center justify-end gap-2">
          <OriginBadge origin={origin} customized={customized} />
          <ActiveToggle
            label={name.ru}
            active={active}
            disabled={!canDeactivate}
            onChange={onActive}
          />
          <RemoveButton label={name.ru} usage={usage} onRemove={onRemove} />
        </div>
      </Td>
    </Tr>
  )
}

export function EnumsTab({ data, selected }: { data: AdminMeta; selected?: string }) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const action = useAdminAction()
  const sets = [
    ...new Set([
      ...data.enumOptions.map((o) => o.setCode),
      ...data.fields.map((f) => f.enumSet).filter((x): x is string => Boolean(x)),
    ]),
  ].sort()
  const set = selected && sets.includes(selected) ? selected : sets[0]
  const options = data.enumOptions.filter((o) => o.setCode === set)
  const usedBy = data.fields.filter((f) => f.enumSet === set).map((f) => f.name.ru)
  const [code, setCode] = useState('')
  const [name, setName] = useState<LocalizedName>({ ru: '' })

  return (
    <div className="grid gap-6 lg:grid-cols-[14rem_1fr]">
      <nav className="flex flex-wrap gap-1 lg:flex-col" aria-label={t('adminMeta.tabs.enums')}>
        {sets.map((s) => (
          <button
            key={s}
            type="button"
            onClick={() => void navigate({ to: '/admin/meta', search: { tab: 'enums', item: s } })}
            aria-current={s === set || undefined}
            className="rounded-md px-3 py-1.5 text-left font-mono text-sm text-muted-foreground hover:bg-accent aria-[current=true]:bg-accent aria-[current=true]:text-foreground"
          >
            {s}
          </button>
        ))}
      </nav>
      {set ? (
        <Card>
          <CardHeader>
            <CardTitle className="font-mono text-base">{set}</CardTitle>
            {usedBy.length ? (
              <p className="text-sm text-muted-foreground">
                {t('adminMeta.enums.usedBy', { fields: usedBy.join(', ') })}
              </p>
            ) : null}
          </CardHeader>
          <CardContent className="grid gap-4">
            <AdminError error={action.error} />
            <Table>
              <THead>
                <Tr>
                  <Th>{t('admin.refFields.name')}</Th>
                  <Th>{t('adminMeta.code')}</Th>
                  <Th>{t('adminMeta.usageColumn')}</Th>
                  <Th />
                </Tr>
              </THead>
              <TBody>
                {options.map((o) => (
                  <Row
                    key={o.code}
                    {...o}
                    onRename={(n) =>
                      action.run('enumOptions', 'update', { setCode: set, code: o.code, name: n })
                    }
                    onActive={(active) =>
                      action.run('enumOptions', 'update', { setCode: set, code: o.code, active })
                    }
                    onRemove={() =>
                      action.run('enumOptions', 'remove', { setCode: set, code: o.code })
                    }
                  />
                ))}
              </TBody>
            </Table>
            <form
              className="grid gap-3 sm:grid-cols-[10rem_1fr_1fr_auto] sm:items-end"
              onSubmit={(e) => {
                e.preventDefault()
                action.run('enumOptions', 'create', { setCode: set, code, name }, () => {
                  setCode('')
                  setName({ ru: '' })
                })
              }}
            >
              <Field label={t('adminMeta.code')}>
                <Input
                  value={code}
                  required
                  pattern="[a-z][a-z0-9_]*"
                  onChange={(e) => setCode(e.target.value)}
                />
              </Field>
              <NameInputs value={name} onChange={setName} />
              <Button type="submit" disabled={action.pending}>
                {t('adminMeta.enums.add')}
              </Button>
            </form>
          </CardContent>
        </Card>
      ) : (
        <p className="text-sm text-muted-foreground">{t('adminMeta.enums.none')}</p>
      )}
    </div>
  )
}

const SYSTEM_STATUSES = new Set(['in_collection', 'wanted', 'for_sale', 'sold'])

export function StatusesTab({ data }: { data: AdminMeta }) {
  const { t } = useTranslation()
  const action = useAdminAction()
  const [code, setCode] = useState('')
  const [name, setName] = useState<LocalizedName>({ ru: '' })
  const [color, setColor] = useState('#64748b')
  const [isOwned, setIsOwned] = useState(true)
  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('adminMeta.tabs.statuses')}</CardTitle>
        <p className="text-sm text-muted-foreground">{t('adminMeta.statuses.hint')}</p>
      </CardHeader>
      <CardContent className="grid gap-4">
        <AdminError error={action.error} />
        <Table>
          <THead>
            <Tr>
              <Th>{t('admin.refFields.name')}</Th>
              <Th>{t('adminMeta.code')}</Th>
              <Th>{t('adminMeta.statuses.color')}</Th>
              <Th>{t('adminMeta.statuses.owned')}</Th>
              <Th>{t('adminMeta.usageColumn')}</Th>
              <Th />
            </Tr>
          </THead>
          <TBody>
            {data.statuses.map((s) => (
              <Row
                key={s.code}
                {...s}
                canDeactivate={!SYSTEM_STATUSES.has(s.code)}
                extra={
                  <>
                    <Td>
                      <input
                        type="color"
                        aria-label={t('adminMeta.statuses.colorFor', { name: s.name.ru })}
                        value={s.color}
                        onChange={(e) =>
                          action.run('statuses', 'update', { code: s.code, color: e.target.value })
                        }
                      />
                    </Td>
                    <Td>
                      <Checkbox
                        aria-label={t('adminMeta.statuses.ownedFor', { name: s.name.ru })}
                        checked={s.isOwned}
                        onChange={(e) =>
                          action.run('statuses', 'update', {
                            code: s.code,
                            isOwned: e.target.checked,
                          })
                        }
                      />
                    </Td>
                  </>
                }
                usage={SYSTEM_STATUSES.has(s.code) ? Math.max(1, s.usage) : s.usage}
                onRename={(n) => action.run('statuses', 'update', { code: s.code, name: n })}
                onActive={(active) => action.run('statuses', 'update', { code: s.code, active })}
                onRemove={() => action.run('statuses', 'remove', { code: s.code })}
              />
            ))}
          </TBody>
        </Table>
        <form
          className="grid gap-3 sm:grid-cols-[10rem_1fr_1fr_auto_auto_auto] sm:items-end"
          onSubmit={(e) => {
            e.preventDefault()
            action.run('statuses', 'create', { code, name, color, isOwned }, () => {
              setCode('')
              setName({ ru: '' })
            })
          }}
        >
          <Field label={t('adminMeta.code')}>
            <Input
              value={code}
              required
              pattern="[a-z][a-z0-9_]*"
              onChange={(e) => setCode(e.target.value)}
            />
          </Field>
          <NameInputs value={name} onChange={setName} />
          <Field label={t('adminMeta.statuses.color')}>
            <input
              type="color"
              className="h-9"
              value={color}
              onChange={(e) => setColor(e.target.value)}
            />
          </Field>
          <label className="flex items-center gap-2 pb-2 text-sm">
            <Checkbox checked={isOwned} onChange={(e) => setIsOwned(e.target.checked)} />
            {t('adminMeta.statuses.owned')}
          </label>
          <Button type="submit" disabled={action.pending}>
            {t('adminMeta.statuses.add')}
          </Button>
        </form>
      </CardContent>
    </Card>
  )
}

export function GradesTab({ data }: { data: AdminMeta }) {
  const { t } = useTranslation()
  const action = useAdminAction()
  const [scaleCode, setScaleCode] = useState('')
  const [scaleName, setScaleName] = useState<LocalizedName>({ ru: '' })
  return (
    <div className="grid gap-6">
      <AdminError error={action.error} />
      {data.gradeScales.map((scale) => (
        <ScaleCard key={scale.code} scale={scale} action={action} />
      ))}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">{t('adminMeta.grades.newScale')}</CardTitle>
        </CardHeader>
        <CardContent>
          <form
            className="grid gap-3 sm:grid-cols-[10rem_1fr_1fr_auto] sm:items-end"
            onSubmit={(e) => {
              e.preventDefault()
              action.run('gradeScales', 'create', { code: scaleCode, name: scaleName }, () => {
                setScaleCode('')
                setScaleName({ ru: '' })
              })
            }}
          >
            <Field label={t('adminMeta.code')}>
              <Input
                value={scaleCode}
                required
                pattern="[a-z][a-z0-9_]*"
                onChange={(e) => setScaleCode(e.target.value)}
              />
            </Field>
            <NameInputs value={scaleName} onChange={setScaleName} />
            <Button type="submit" disabled={action.pending}>
              {t('adminMeta.create')}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  )
}

function ScaleCard({
  scale,
  action,
}: {
  scale: AdminMeta['gradeScales'][number]
  action: ReturnType<typeof useAdminAction>
}) {
  const { t } = useTranslation()
  const [code, setCode] = useState('')
  const [name, setName] = useState<LocalizedName>({ ru: '' })
  const [rank, setRank] = useState('')
  return (
    <Card>
      <CardHeader className="flex flex-row flex-wrap items-center gap-3">
        <CardTitle className="mr-auto text-base">
          {scale.name.ru}{' '}
          <span className="font-mono text-sm text-muted-foreground">{scale.code}</span>
        </CardTitle>
        <OriginBadge origin={scale.origin} customized={scale.customized} />
        <ActiveToggle
          label={scale.name.ru}
          active={scale.active}
          onChange={(active) => action.run('gradeScales', 'update', { code: scale.code, active })}
        />
      </CardHeader>
      <CardContent className="grid gap-4">
        <p className="text-sm text-muted-foreground">{t('adminMeta.grades.rankHint')}</p>
        <Table>
          <THead>
            <Tr>
              <Th>{t('admin.refFields.name')}</Th>
              <Th>{t('adminMeta.code')}</Th>
              <Th>{t('adminMeta.grades.rank')}</Th>
              <Th>{t('adminMeta.usageColumn')}</Th>
              <Th />
            </Tr>
          </THead>
          <TBody>
            {scale.values.map((v) => (
              <Row
                key={v.code}
                {...v}
                extra={
                  <Td>
                    <Input
                      className="w-20"
                      type="number"
                      min={0}
                      max={100}
                      aria-label={t('adminMeta.grades.rankFor', { name: v.name.ru })}
                      defaultValue={v.rank}
                      onBlur={(e) => {
                        const r = Number(e.target.value)
                        if (Number.isInteger(r) && r !== v.rank)
                          action.run('gradeScales', 'updateValue', {
                            scale: scale.code,
                            code: v.code,
                            rank: r,
                          })
                      }}
                    />
                  </Td>
                }
                onRename={(n) =>
                  action.run('gradeScales', 'updateValue', {
                    scale: scale.code,
                    code: v.code,
                    name: n,
                  })
                }
                onActive={(active) =>
                  action.run('gradeScales', 'updateValue', {
                    scale: scale.code,
                    code: v.code,
                    active,
                  })
                }
                onRemove={() =>
                  action.run('gradeScales', 'removeValue', { scale: scale.code, code: v.code })
                }
              />
            ))}
          </TBody>
        </Table>
        <form
          className="grid gap-3 sm:grid-cols-[8rem_1fr_1fr_6rem_auto] sm:items-end"
          onSubmit={(e) => {
            e.preventDefault()
            action.run(
              'gradeScales',
              'createValue',
              { scale: scale.code, code, name, rank: Number(rank) },
              () => {
                setCode('')
                setName({ ru: '' })
                setRank('')
              },
            )
          }}
        >
          <Field label={t('adminMeta.code')}>
            <Input value={code} required maxLength={32} onChange={(e) => setCode(e.target.value)} />
          </Field>
          <NameInputs value={name} onChange={setName} />
          <Field label={t('adminMeta.grades.rank')}>
            <Input
              type="number"
              min={0}
              max={100}
              required
              value={rank}
              onChange={(e) => setRank(e.target.value)}
            />
          </Field>
          <Button type="submit" disabled={action.pending}>
            {t('adminMeta.grades.addValue')}
          </Button>
        </form>
      </CardContent>
    </Card>
  )
}
